import React from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, Stack, Text } from '@atlaskit/primitives';
import type { OptionType } from '@atlaskit/select';
import { FocusAreaIcon } from '@atlassian/focus-area-icon';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled, usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { filter as focusAreaUnderFilter } from '../../../../../controllers/store/search/filters/focus-area-under';

import { i18n } from './messages';

const formatOptionLabel = (option: OptionType) => {
	const { label, metadata } = option;
	const type = metadata?.type || '';
	const owner = metadata?.owner || '';
	const status = metadata?.status;
	const health = metadata?.health;

	return (
		<Inline space="space.150" alignBlock="center">
			<FocusAreaIcon spacing="spacious" status={status} health={health} label={label} />
			<Stack>
				<Text maxLines={2}>{label}</Text>
				<Text color="color.text.subtle" size="small">
					{type}
					{' • '}
					{owner}
				</Text>
			</Stack>
		</Inline>
	);
};

const FocusAreaUnderFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({ filterId: focusAreaUnderFilter.id });

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		options,
		isLookupDefined,
		lookupInput,
		isLookupActive,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.mercuryFilterFocusAreaUnderLabelLabelSelected),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.mercuryFilterFocusAreaUnderLabelUnselected),
			options: suggestedOptions,
		},
	];

	const label =
		fit === 'compact' && selectedOptions.length > 0
			? ''
			: formatMessage(i18n.mercuryFilterFocusAreaUnderLabel);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			placeholder={formatMessage(i18n.mercuryFilterFocusAreaUnderLabelPlaceholder)}
			filterOrControlId={'focusAreaUnder'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			formatOptionLabel={formatOptionLabel}
			onClearOptions={clearOptions}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default FocusAreaUnderFilter;
